<template>
    <div class="category-menu">
        <button
            v-for="category in categories"
            :key="category"
            class="category-menu-item"
            :class="{
                focused,
                selected: isCategorySelected(category),
                disabled: isCategoryDisabled(category)
            }"
            @click="onClickCategory(category)">
            <pendo-icon
                class="category-menu-item__icon"
                :type="getItemCategoryIcon(category)"
                size="14" />
            <span class="category-menu-item__label">{{ getItemCategoryLabel(category) }}</span>
            <pendo-loading-indicator
                v-if="isCategoryLoading(category)"
                type="feather"
                size="small"
                :spinner-props="{ color: getSpinnerColor(category) }" />
            <pendo-icon
                v-else-if="isCategoryRejected(category)"
                class="category-menu-item__error"
                type="x-circle"
                size="14" />
            <span
                v-else-if="isCategoryResolved(category)"
                class="category-menu-item__tag">{{ countByCategory[category] }}</span>
            <pendo-icon
                class="category-menu-item__arrow"
                type="chevron-right"
                size="14" />
        </button>
    </div>
</template>

<script>
import { PendoIcon, PendoLoadingIndicator } from '@pendo/components';
import { getItemCategoryIcon, getItemCategoryLabel } from '@/ask-pendo/components/mentions-popover/items.utils';
import { LOADING_STATES } from '@/constants';

const { LOADING, REJECTED, RESOLVED } = LOADING_STATES;

export default {
    name: 'MentionsPaneCategories',
    components: {
        PendoIcon,
        PendoLoadingIndicator,
    },
    expose: ['onKeyDown'],
    props: {
        activeCategory: {
            type: String,
            required: true,
        },
        categories: {
            type: Array,
            required: true,
        },
        countByCategory: {
            type: Object,
            required: true,
        },
        focused: {
            type: Boolean,
            required: true,
        },
        loadingStateByCategory: {
            type: Object,
            required: true,
        },
        preventDisablingCategories: {
            type: Array,
            default: () => []
        }
    },
    emits: ['enter', 'focus', 'select'],
    methods: {
        getItemCategoryLabel,
        getItemCategoryIcon,
        getSpinnerColor (category) {
            return this.focused && this.isCategorySelected(category)
                ? 'var(--palette-gray-0)'
                : undefined;
        },
        isCategoryDisabled (category) {
            return this.countByCategory[category] === 0 && !this.preventDisablingCategories.includes(category);
        },
        isCategoryLoading (category) {
            return this.loadingStateByCategory[category] === LOADING;
        },
        isCategoryRejected (category) {
            return this.loadingStateByCategory[category] === REJECTED;
        },
        isCategoryResolved (category) {
            return this.loadingStateByCategory[category] === RESOLVED;
        },
        isCategorySelected (category) {
            return this.activeCategory === category;
        },
        onClickCategory (category) {
            if (!this.isCategoryDisabled(category)) {
                this.$emit('select', category);
            }
        },
        onKeyDown (event) {
            this.$emit('focus');

            if (event.key === 'ArrowDown') {
                this.selectDown();

                return true;
            }

            if (event.key === 'ArrowUp') {
                this.selectUp();

                return true;
            }

            if (event.key === 'Enter') {
                this.$emit('enter', this.activeCategory);

                return true;
            }
        },
        selectNext (nextIndex) {
            const currentIndex = this.categories.indexOf(this.activeCategory);
            if (this.categories.length === 0 || currentIndex === -1) return;

            // Select next until we find a category that isn't disabled (or we loop back to the current category)
            let targetIndex = nextIndex(currentIndex);
            while (targetIndex !== currentIndex && this.isCategoryDisabled(this.categories[targetIndex])) {
                targetIndex = nextIndex(targetIndex);
            }

            // If all categories empty, allow selecting empty categories
            if (targetIndex === currentIndex) {
                targetIndex = nextIndex(currentIndex);
            }

            this.$emit('select', this.categories[targetIndex]);
        },
        selectDown () {
            this.selectNext((currentIndex) => (currentIndex + 1) % this.categories.length);
        },
        selectUp () {
            this.selectNext((currentIndex) => (currentIndex === 0 ? this.categories.length - 1 : currentIndex - 1));
        },
    },
};
</script>

<style scoped lang="less">
.category-menu {
    min-width: 220px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-right: 1px solid var(--palette-gray-30);
    padding-top: 8px;
}

.category-menu-item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    height: 32px;

    padding: 0 8px;
    background: none;
    border: none;
    color: var(--palette-gray-90);

    text-align: left;
    cursor: pointer;

    &:first-of-type {
        border-bottom: 1px solid var(--palette-gray-30);
    }

    &:not(.disabled):hover {
        background-color: var(--palette-gray-20);
    }

    &.selected {
        background-color: var(--palette-gray-30);
    }

    &.selected.focused {
        color: var(--palette-gray-0);
        background-color: var(--palette-teal-70);
    }

    &.disabled {
        color: var(--palette-gray-50);
        cursor: default;
    }

    &__icon {
        flex-shrink: 0;
    }

    &__error {
        color: var(--palette-red-70);
    }

    &.selected.focused &__error {
        color: var(--palette-gray-0);
    }

    &__label {
        flex: 1;
        line-height: 150%;
    }

    &__tag {
        margin-left: auto;
        padding: 4px;

        line-height: 100%;
        border-radius: 3px;
        background-color: var(--palette-gray-20);
    }

    &.selected.focused &__tag {
        background-color: var(--palette-teal-60);
    }

    &__arrow {
        pointer-events: none;
        display: flex;
        align-items: center;
    }

    &.selected.focused &__arrow {
        color: var(--palette-gray-0);
    }
}
</style>